















import { Component, InjectReactive, Prop, Vue } from 'vue-property-decorator';

import {
	BillingApi,
	OrgsApi,
	Product,
	ProductStatus,
	SubscriptionItem,
	UpdateBilling,
} from '@/api';
import { getProductBillingAttributes, ProductOrdwayAttributes } from '@/models/ordway';
import { OrdwayService } from '@/services/ordway-service';
import { AxiosResponse, AxiosError } from 'axios';

import BillingSetupButton from '@/components/billing-setup-form/billing-setup-button.vue';
import BillingSetupModal from '@/components/billing-setup-form/billing-setup-modal.vue';
import ExternalLinkButton from '@/components/elements/buttons/external-link-button.vue';
import Toast from './toast.vue';
import PlanRow from '@/components/display/information-rows/plan-row.vue';
import InformationRow from '@/components/display/information-row.vue';
import TrialRow from '@/components/display/information-rows/trial-row.vue';
import DirectRow from '@/components/display/information-rows/direct-row.vue';
import { FeedProduct } from '@/pages/account/products/feed/models';

@Component({
	components: {
		DirectRow,
		TrialRow,
		InformationRow,
		PlanRow,
		BillingSetupButton,
		BillingSetupModal,
		ExternalLinkButton,
	},
})
export default class ProductBillingDisplay extends Vue {
	@Prop({ required: true, type: Object })
	private product!: Product & { subscriptionitem: SubscriptionItem };

	@InjectReactive('feedProduct')
	public feedProduct!: FeedProduct;

	public productOrdwayAttributes: ProductOrdwayAttributes | null = null;
	public currentBilling: (UpdateBilling & { partnerName?: string | undefined }) | null = null;
	public loading: boolean = true;
	private toast: Toast = new Toast();

	public get billingAttributes() {
		return getProductBillingAttributes(
			this.productOrdwayAttributes,
			null,
			this.product.subscriptionitem,
		);
	}

	public async created(): Promise<void> {
		await this.fetchProductOrdwayAttribute();
		await this.loadBilling();
		this.loading = false;
		// Allow DOM render after we toggle loading or else $refs is empty. https://vuejs.org/api/general.html#nexttick
		await Vue.nextTick();
		if (
			this.productOrdwayAttributes === null &&
			this.product.status !== ProductStatus.Cancelled &&
			this.product.status !== ProductStatus.Removed
		) {
			(this.$refs.billingSetupButton as BillingSetupButton).openEditBillingModal();
		}
	}

	private async fetchProductOrdwayAttribute(): Promise<void> {
		if (!this.product.id || !this.product.orgId) {
			return;
		}
		try {
			const productOrdwayAttributeResponse: AxiosResponse<ProductOrdwayAttributes> = await OrdwayService.fetchProductOrdwayAttributes(
				this.product.id,
				this.product.orgId,
			);
			this.productOrdwayAttributes = productOrdwayAttributeResponse.data;
		} catch (errorResponse) {
			const error: AxiosError = errorResponse as AxiosError;
			if (error.response) {
				const errorMessage = error.response.data.errors;
				new Toast().makeToast(errorMessage, 'No plan found for this product', 'danger', false);
			} else {
				new Toast().makeToast(
					'There was a problem loading the Ordway attributes for this product, please let the software team know',
					'Error loading Ordway attributes',
					'danger',
					true,
				);
			}
		}
	}

	private async loadBilling(): Promise<void> {
		if (!this.product.id || !this.product.type) {
			return;
		}
		try {
			const billingData = await new BillingApi().currentBilling(this.product.id);
			this.currentBilling = billingData.data.data ?? null;
			if (this.currentBilling && this.currentBilling.partnerId !== null) {
				const partners = await new OrgsApi().orgGetBillingPartnersByProductType(this.product.type);
				const partner = partners.data.data?.find(
					partner => partner.id == this.currentBilling!.partnerId,
				);
				if (partner) {
					this.currentBilling = {
						...this.currentBilling,
						partnerName: partner.name,
					};
				}
			}
		} catch (e) {
			const error: AxiosError = e as AxiosError;
			const errorMessage: string =
				error.response?.data.message ??
				`${error.response?.data.errors ?? 'Failed to get ordway attributes'}`;
			this.toast.makeToast(errorMessage, 'Error', 'danger');
		}
	}

	public get partnerContribution(): string {
		return `${
			this.currentBilling && this.currentBilling.partnerContributionTier
				? `${this.$options.filters?.capitalise(
						this.$options.filters?.decamelize(this.currentBilling.partnerContributionTier),
				  )} | ${this.currentBilling.partnerName}`
				: 'No Contribution'
		}`;
	}
}
